'use strict';

/**
 * What a sample's tests and their documents say, as pure functions over records already loaded: no server or DOM
 * access, so the same answers come out in a browser bundle and in Node. Sample-Scope.js loads the records.
 */

const libTestResultOptions = require('headlight-dynamic-inputs-bundle/source/providers/_TestResultOptions.js');

// Test result code -> colour tone; pass / fail as the config app's Tests tab groups them (web/headlight-platform-configuration/source/samples/SampleWorkspace-Provider.js _tiStatusClass).
const RESULT_TONES = { PASS: 'success', PASC: 'success', SQTY: 'success', FAIL: 'danger', FAIC: 'danger', PROG: 'warning', PEND: 'warning', VOID: 'muted' };

/**
 * @typedef {Object} FormTestCatalog
 * @property {Record<String, { FormName: String, TestHashes: Array<String>, StoryRole?: String }>} Types - DocumentType (RNI Hash) -> the form's name, the tests it declares and its getRNIStoryRole.
 * @property {Record<String, { Name: String }>} Tests - Test.Hash -> the test.
 */

/**
 * @typedef {Object} DocumentTest
 * @property {String} Hash - Test.Hash, e.g. `TR327Av1`.
 * @property {String} Code - The hash as a lab reads it, e.g. `TR 327A`.
 * @property {String} Name - Test.Name, or '' when it only repeats the hash.
 * @property {boolean} Assigned - A test instance of this sample is joined to the document.
 * @property {String} Result - The test instance's result as text (`Passed`), or '' when none is recorded.
 * @property {String} ResultTone - 'success' | 'danger' | 'warning' | 'info' | 'muted' | ''.
 */

/**
 * A test hash as lab staff write it: the version suffix dropped and a space before the number (`TR327Av1` -> `TR 327A`,
 * `AASHTOMP41` -> `AASHTO MP 41`).
 * Hashes that don't fit that shape come back unchanged.
 *
 * @param {String} pHash
 * @return {String}
 */
function formatTestCode(pHash)
{
	const tmpHash = String(pHash || '').trim();
	const tmpMatch = /^([A-Za-z]+)(\d[0-9A-Za-z.-]*?)(v\d+)?$/.exec(tmpHash);
	if (!tmpMatch)
	{
		return tmpHash;
	}
	const tmpAASHTO = /^(AASHTO)([A-Z]+)$/.exec(tmpMatch[1]);
	if (tmpAASHTO)
	{
		return `${tmpAASHTO[1]} ${tmpAASHTO[2]} ${tmpMatch[2]}`;
	}
	return `${tmpMatch[1]} ${tmpMatch[2]}`;
}

/**
 * @param {Object} pRNI - a ReportNamedInstance row
 * @return {?Object} The RNI's options, parsed when stored as JSON; null when there are none.
 */
function getRNIOptions(pRNI)
{
	let tmpOptions = pRNI && (pRNI.Options || pRNI.OptionsJSON);
	if (typeof tmpOptions === 'string')
	{
		try
		{
			tmpOptions = JSON.parse(tmpOptions);
		}
		catch (pError)
		{
			tmpOptions = null;
		}
	}
	return (tmpOptions && (typeof tmpOptions === 'object')) ? tmpOptions : null;
}

/**
 * The test hashes an RNI's options declare for its form: `TestHashes` for a multi-test form, else a `TestHash` that names
 * a real test (multi-test forms set `TestHash` to the form's own hash, which the caller filters out against the Test list).
 *
 * @param {Object} pRNI - a ReportNamedInstance row
 * @return {Array<String>}
 */
function getRNITestHashes(pRNI)
{
	const tmpOptions = getRNIOptions(pRNI);
	if (!tmpOptions)
	{
		return [];
	}
	const tmpHashes = Array.isArray(tmpOptions.TestHashes) ? tmpOptions.TestHashes : [ tmpOptions.TestHash ];
	return [ ...new Set(tmpHashes.filter((pHash) => (typeof pHash === 'string') && (pHash.length > 0))) ];
}

/**
 * Where a form's documents sit in a sample's story: the form that creates the sample, or the one that authorizes it.
 * Read from the RNI's role, never its name or hash: `Options.SampleCreation` (the role Fieldbook's create flow reads) or
 * a form built on the SampleCreation schema, and a form built on the SampleApproval schema (ManifestSchema 390).
 *
 * @param {Object} pRNI - a ReportNamedInstance row
 * @return {String} 'creation' | 'authorization' | ''
 */
function getRNIStoryRole(pRNI)
{
	const tmpOptions = getRNIOptions(pRNI);
	if (!tmpOptions)
	{
		return '';
	}
	if (tmpOptions.SampleCreation || (tmpOptions.FormManifestSchemaHash === 'SampleCreation'))
	{
		return 'creation';
	}
	if (tmpOptions.FormManifestSchemaHash === 'SampleApproval')
	{
		return 'authorization';
	}
	return '';
}

const STORY_RANK = { 'creation': 0, '': 1, 'authorization': 2 };

/**
 * A sample's documents in the order its story runs: the creation form first, the test forms and anything else next,
 * the authorization last; oldest first within each, then by ID. Documents whose form is not in the catalog yet sit in
 * the middle.
 *
 * @param {Array<{ IDDocument: number, DocumentType?: String, CreateDate?: String }>} pDocuments
 * @param {?FormTestCatalog} pCatalog
 * @return {Array<Object>} A new array; the input is left as it was.
 */
function orderDocumentStory(pDocuments, pCatalog)
{
	const tmpTypes = (pCatalog && pCatalog.Types) || {};
	const tmpRank = (pDocument) =>
	{
		const tmpType = tmpTypes[pDocument.DocumentType];
		return STORY_RANK[(tmpType && tmpType.StoryRole) || ''];
	};
	const tmpTime = (pDocument) =>
	{
		const tmpParsed = Date.parse(pDocument.CreateDate || '');
		return Number.isNaN(tmpParsed) ? Number.MAX_SAFE_INTEGER : tmpParsed;
	};
	return (Array.isArray(pDocuments) ? pDocuments : []).slice().sort((pA, pB) =>
		(tmpRank(pA) - tmpRank(pB)) || (tmpTime(pA) - tmpTime(pB)) || (Number(pA.IDDocument) - Number(pB.IDDocument)));
}

/**
 * A stored test result code as text and a colour tone, the same pass / fail grouping as the Tests tab's status chip.
 * Codes outside the shared result list (and N/A) show nothing.
 *
 * @param {String} pCode - TestInstance.TestResult, e.g. `PASC`
 * @return {{ Result: String, ResultTone: String }}
 */
function getTestResult(pCode)
{
	const tmpCode = String(pCode || '').trim().toUpperCase();
	const tmpKnown = libTestResultOptions.DEFAULT_TEST_RESULT_OPTIONS.some((pOption) => pOption.id === tmpCode);
	if (!tmpKnown || (tmpCode === 'N/A'))
	{
		return { Result: '', ResultTone: '' };
	}
	return { Result: libTestResultOptions.getTestResultLabel(tmpCode), ResultTone: RESULT_TONES[tmpCode] || 'info' };
}

/**
 * @typedef {Object} TestInstanceFormValues
 * @property {String} Result - The result to show: the one a linked form holds for approval, else the instance's own.
 * @property {?Object} ResultDocument - The unapproved document holding Result, or null when Result is the instance's own.
 * @property {String} Notes - The remarks to show, chosen the same way.
 * @property {?Object} NotesDocument - The unapproved document holding Notes, or null.
 */

/**
 * The values a document holds back for a test instance until it is approved: its `OnEnteringState.Approved` comprehension
 * (the one test forms use), else its `OnApprovalAction.Approve` one. The Sample Authorization form's Test Summary
 * (ManifestSchema `SampleApproval`) reads the same two places; keep them in step.
 *
 * @param {Object} pDocument
 * @param {String} pGUIDTestInstance
 * @return {Object} The held-back TestInstance fields, or {} when the document holds none.
 */
function getHeldTestInstanceValues(pDocument, pGUIDTestInstance)
{
	const tmpEntityData = (pDocument && pDocument.EntityData) || {};
	const fHeld = (pTrigger, pState) =>
	{
		const tmpTrigger = tmpEntityData[pTrigger] && tmpEntityData[pTrigger][pState];
		return (tmpTrigger && tmpTrigger.TestInstance && tmpTrigger.TestInstance[pGUIDTestInstance]) || null;
	};
	return fHeld('OnEnteringState', 'Approved') || fHeld('OnApprovalAction', 'Approve') || {};
}

/**
 * What the Tests tab shows for a test instance's result and remarks: while a linked document is not approved yet, the
 * values it will write on approval; otherwise the instance's own. The first unapproved document holding a value wins.
 *
 * @param {{ GUIDTestInstance?: String, Status?: String, TestResult?: String, Notes?: String }} pTestInstance
 * @param {Array<Object>} pDocuments - the documents linked to the instance
 * @return {TestInstanceFormValues}
 */
function getTestInstanceFormValues(pTestInstance, pDocuments)
{
	const tmpInstance = pTestInstance || {};
	const tmpValues = { Result: tmpInstance.Status || tmpInstance.TestResult || '', ResultDocument: null, Notes: tmpInstance.Notes || '', NotesDocument: null };
	if (!tmpInstance.GUIDTestInstance)
	{
		return tmpValues;
	}
	for (const tmpDocument of (Array.isArray(pDocuments) ? pDocuments : []))
	{
		if (!tmpDocument || isDocumentApproved(tmpDocument))
		{
			continue;
		}
		const tmpHeld = getHeldTestInstanceValues(tmpDocument, tmpInstance.GUIDTestInstance);
		if (!tmpValues.ResultDocument && tmpHeld.TestResult)
		{
			tmpValues.Result = String(tmpHeld.TestResult);
			tmpValues.ResultDocument = tmpDocument;
		}
		if (!tmpValues.NotesDocument && tmpHeld.Notes)
		{
			tmpValues.Notes = String(tmpHeld.Notes);
			tmpValues.NotesDocument = tmpDocument;
		}
	}
	return tmpValues;
}

/**
 * This sample's test instances joined to a document, by test hash, with the first recorded result for each.
 *
 * @param {number} pIDDocument
 * @param {{ TestInstances?: Array<Object>, TestMap?: Record<String, Object>, TestInstanceDocumentMap?: Record<String, Array<Object>> }} pBundle
 * @return {Array<{ Hash: String, ResultCode: String }>}
 */
function getAssignedTests(pIDDocument, pBundle)
{
	const tmpBundle = pBundle || {};
	const tmpDocumentMap = tmpBundle.TestInstanceDocumentMap || {};
	const tmpTestMap = tmpBundle.TestMap || {};
	const tmpAssigned = [];
	for (const tmpInstance of (tmpBundle.TestInstances || []))
	{
		const tmpDocuments = tmpDocumentMap[tmpInstance.IDTestInstance] || [];
		const tmpTest = tmpTestMap[tmpInstance.IDTest];
		if (!tmpTest || !tmpTest.Hash || !tmpDocuments.some((pDocument) => Number(pDocument.IDDocument) === Number(pIDDocument)))
		{
			continue;
		}
		const tmpExisting = tmpAssigned.find((pEntry) => pEntry.Hash === tmpTest.Hash);
		if (!tmpExisting)
		{
			tmpAssigned.push({ Hash: tmpTest.Hash, ResultCode: tmpInstance.TestResult || '' });
		}
		else if (!tmpExisting.ResultCode && tmpInstance.TestResult)
		{
			tmpExisting.ResultCode = tmpInstance.TestResult;
		}
	}
	return tmpAssigned;
}

/**
 * The tests to show for a document: those its form declares (that exist as tests), in the form's order, then any other
 * test this sample assigned to it.
 *
 * @param {{ IDDocument: number, DocumentType?: String }} pDocument
 * @param {?FormTestCatalog} pCatalog
 * @param {Object} pBundle - the sample's test instance bundle (see getAssignedTests)
 * @return {Array<DocumentTest>}
 */
function getDocumentTests(pDocument, pCatalog, pBundle)
{
	const tmpCatalog = pCatalog || { Types: {}, Tests: {} };
	const tmpType = tmpCatalog.Types[pDocument.DocumentType];
	const tmpAssignedTests = getAssignedTests(pDocument.IDDocument, pBundle);
	const tmpAssigned = tmpAssignedTests.map((pEntry) => pEntry.Hash);
	const tmpTestMap = (pBundle && pBundle.TestMap) || {};
	const tmpNameByHash = {};
	for (const tmpTest of Object.values(tmpTestMap))
	{
		tmpNameByHash[tmpTest.Hash] = tmpTest.Name;
	}
	const tmpDeclared = (tmpType ? tmpType.TestHashes : []).filter((pHash) => tmpCatalog.Tests[pHash] || (tmpAssigned.indexOf(pHash) > -1));
	const tmpHashes = tmpDeclared.concat(tmpAssigned.filter((pHash) => tmpDeclared.indexOf(pHash) < 0));
	return tmpHashes.map((pHash) =>
	{
		const tmpName = (tmpCatalog.Tests[pHash] && tmpCatalog.Tests[pHash].Name) || tmpNameByHash[pHash] || '';
		const tmpAssignedTest = tmpAssignedTests.find((pEntry) => pEntry.Hash === pHash);
		return Object.assign(
			{ Hash: pHash, Code: formatTestCode(pHash), Name: (tmpName && (tmpName !== pHash)) ? tmpName : '', Assigned: !!tmpAssignedTest },
			getTestResult(tmpAssignedTest ? tmpAssignedTest.ResultCode : ''));
	});
}

/**
 * @typedef {Object} TestBundle
 * @property {Array<Object>} TestInstances - The sample's test instances.
 * @property {Record<String, Object>} TestMap - IDTest -> Test.
 * @property {Record<String, Array<Object>>} TestInstanceDocumentMap - IDTestInstance -> the documents linked to it, each once.
 */

/**
 * @typedef {Object} TestSummaryDocument
 * @property {Object} Document - The linked document.
 * @property {String} Status - Document.Status, the form's approval status (`Draft`, `Submitted`, `Approved` ...).
 * @property {String} StatusTone - See getDocumentStatusTone.
 */

/**
 * @typedef {Object} TestSummaryRow
 * @property {Object} TestInstance
 * @property {?Object} Test - The test instance's Test, or null when it was not loaded.
 * @property {String} Name - The test's name, else the instance's, else `Test #<IDTestInstance>`.
 * @property {String} Result - See getTestInstanceFormValues.
 * @property {boolean} ResultPending - Result comes from a linked form that is not approved yet.
 * @property {?Object} ResultDocument - That form, or null.
 * @property {String} Notes - See getTestInstanceFormValues.
 * @property {boolean} NotesPending
 * @property {?Object} NotesDocument
 * @property {Array<TestSummaryDocument>} Documents - The documents linked to the instance, in bundle order.
 */

/**
 * Whether a document's held-back values have been written to the records they name. Test forms hold them under
 * `OnEnteringState.Approved`, so only `Approved` counts.
 *
 * @param {Object} pDocument
 * @return {boolean}
 */
function isDocumentApproved(pDocument)
{
	return !!pDocument && (pDocument.Status === 'Approved');
}

/**
 * A document status as a colour tone: approved and authorized forms are `success`, rejected ones `danger`, the rest
 * `info`; no status is ''.
 *
 * @param {String} pStatus - Document.Status
 * @return {String} 'success' | 'danger' | 'info' | ''
 */
function getDocumentStatusTone(pStatus)
{
	if (!pStatus)
	{
		return '';
	}
	if ((pStatus === 'Approved') || (pStatus === 'Authorized'))
	{
		return 'success';
	}
	if (pStatus === 'Rejected')
	{
		return 'danger';
	}
	return 'info';
}

/**
 * Assemble a sample's test bundle from the loaded records. A document is linked to a test instance by a
 * `DocumentPolyJoin` (RemoteType `TestInstance`), or, for a document joined to the sample, by naming the instance's
 * GUID in its `EntityData.TestInstance` list (`{IDDocument}` in the GUID stands for the document's own ID); some
 * customers' forms only link that way.
 *
 * @param {Object} pRecords
 * @param {Array<Object>} [pRecords.TestInstances]
 * @param {Array<Object>} [pRecords.Tests]
 * @param {Array<{ IDRemote: number, IDDocument: number }>} [pRecords.TestInstanceDocumentJoins] - DocumentPolyJoins to the test instances
 * @param {Array<{ IDDocument: number }>} [pRecords.SampleDocumentJoins] - DocumentPolyJoins to the sample
 * @param {Array<Object>} [pRecords.Documents] - every document either join names
 * @return {TestBundle}
 */
function buildTestBundle(pRecords)
{
	const tmpRecords = pRecords || {};
	const tmpTestInstances = Array.isArray(tmpRecords.TestInstances) ? tmpRecords.TestInstances : [];
	const tmpTestMap = {};
	for (const tmpTest of (tmpRecords.Tests || []))
	{
		tmpTestMap[tmpTest.IDTest] = tmpTest;
	}
	const tmpDocumentByID = {};
	for (const tmpDocument of (tmpRecords.Documents || []))
	{
		tmpDocumentByID[tmpDocument.IDDocument] = tmpDocument;
	}
	/** @type {Record<String, Array<Object>>} */
	const tmpDocumentMap = {};
	const tmpIDByGUID = {};
	for (const tmpInstance of tmpTestInstances)
	{
		tmpDocumentMap[tmpInstance.IDTestInstance] = [];
		if (typeof tmpInstance.GUIDTestInstance === 'string')
		{
			tmpIDByGUID[tmpInstance.GUIDTestInstance] = tmpInstance.IDTestInstance;
		}
	}
	const fLink = (pIDTestInstance, pDocument) =>
	{
		const tmpLinked = tmpDocumentMap[pIDTestInstance];
		if (pDocument && tmpLinked && (tmpLinked.indexOf(pDocument) < 0))
		{
			tmpLinked.push(pDocument);
		}
	};
	for (const tmpJoin of (tmpRecords.TestInstanceDocumentJoins || []))
	{
		fLink(tmpJoin.IDRemote, tmpDocumentByID[tmpJoin.IDDocument]);
	}
	for (const tmpJoin of (tmpRecords.SampleDocumentJoins || []))
	{
		const tmpDocument = tmpDocumentByID[tmpJoin.IDDocument];
		const tmpNamed = tmpDocument && tmpDocument.EntityData && tmpDocument.EntityData.TestInstance;
		if (!Array.isArray(tmpNamed))
		{
			continue;
		}
		for (const tmpEntry of tmpNamed)
		{
			if (!tmpEntry || (typeof tmpEntry.GUIDTestInstance !== 'string'))
			{
				continue;
			}
			const tmpGUID = tmpEntry.GUIDTestInstance.replace('{IDDocument}', String(tmpDocument.IDDocument));
			if (tmpIDByGUID.hasOwnProperty(tmpGUID))
			{
				fLink(tmpIDByGUID[tmpGUID], tmpDocument);
			}
		}
	}
	return { TestInstances: tmpTestInstances, TestMap: tmpTestMap, TestInstanceDocumentMap: tmpDocumentMap };
}

/**
 * One row per test instance with what a screen listing a sample's tests shows: the result and remarks (a linked
 * form's pending values first, see getTestInstanceFormValues) and each linked document's form status.
 *
 * @param {TestBundle} pBundle
 * @return {Array<TestSummaryRow>}
 */
function buildTestSummary(pBundle)
{
	/** @type {Partial<TestBundle>} */
	const tmpBundle = pBundle || {};
	const tmpTestMap = tmpBundle.TestMap || {};
	const tmpDocumentMap = tmpBundle.TestInstanceDocumentMap || {};
	return (tmpBundle.TestInstances || []).map((pInstance) =>
	{
		const tmpTest = tmpTestMap[pInstance.IDTest] || null;
		const tmpDocuments = tmpDocumentMap[pInstance.IDTestInstance] || [];
		const tmpValues = getTestInstanceFormValues(pInstance, tmpDocuments);
		return {
			TestInstance: pInstance,
			Test: tmpTest,
			Name: (tmpTest && tmpTest.Name) || pInstance.Name || `Test #${pInstance.IDTestInstance}`,
			Result: tmpValues.Result,
			ResultPending: !!tmpValues.ResultDocument,
			ResultDocument: tmpValues.ResultDocument,
			Notes: tmpValues.Notes,
			NotesPending: !!tmpValues.NotesDocument,
			NotesDocument: tmpValues.NotesDocument,
			Documents: tmpDocuments.map((pDocument) => ({ Document: pDocument, Status: pDocument.Status || '', StatusTone: getDocumentStatusTone(pDocument.Status) })),
		};
	});
}

module.exports =
{
	formatTestCode,
	getRNIOptions,
	getRNITestHashes,
	getRNIStoryRole,
	orderDocumentStory,
	getTestResult,
	getHeldTestInstanceValues,
	getTestInstanceFormValues,
	isDocumentApproved,
	getDocumentStatusTone,
	getAssignedTests,
	getDocumentTests,
	buildTestBundle,
	buildTestSummary,
};
